import { useId, useMemo } from 'react';
import { buildSigilKnot } from '@sorrem/shared';
import { sigilRunPath, splitSigilRuns, trimSigilRun } from './sigil-runs';

interface InkSigilProps {
    /** The character id; the same id always draws the same knot as their `Sigil`. */
    readonly seed: string;
    /** Width and height in CSS px. Defaults to filling its box. */
    readonly size?: number;
    readonly className?: string;
}

const KNOT_RADIUS = 90;
const PEN_WIDTH = 3.4;
/** How wide the break is where a strand passes under another. */
const CROSSING_GAP = 12;
/** Samples trimmed from each end of the gap, so it never notches the strand it continues. */
const GAP_TRIM = 3;

/**
 * A character's sigil drawn by hand in ink: the same woven knot as `Sigil`,
 * as one pen line that breaks wherever it passes under itself. Drawn in
 * `currentColor`, with a slight wobble so it reads as handmade, not printed.
 */
export function InkSigil({ seed, size, className }: InkSigilProps): JSX.Element {
    const id = useId().replace(/:/g, '');
    const runs = useMemo(() => splitSigilRuns(buildSigilKnot(seed).points), [seed]);

    return (
        <svg className={className} width={size} height={size} viewBox="-110 -110 220 220" fill="none" aria-hidden="true">
            <defs>
                <filter id={`ink-sigil-wobble-${id}`} x="-10%" y="-10%" width="120%" height="120%">
                    <feTurbulence type="fractalNoise" baseFrequency="0.035" numOctaves={2} seed={7} />
                    <feDisplacementMap in="SourceGraphic" scale={2.6} xChannelSelector="R" yChannelSelector="G" />
                </filter>
                {/* Cuts each under-strand where an over-strand crosses it. */}
                <mask id={`ink-sigil-gaps-${id}`} maskUnits="userSpaceOnUse" x={-110} y={-110} width={220} height={220}>
                    <rect x={-110} y={-110} width={220} height={220} fill="white" />
                    {runs.over.map((run, index) => (
                        <path key={index} d={sigilRunPath(trimSigilRun(run, GAP_TRIM), KNOT_RADIUS)} stroke="black" strokeWidth={CROSSING_GAP} />
                    ))}
                </mask>
            </defs>

            <g filter={`url(#ink-sigil-wobble-${id})`} stroke="currentColor" strokeWidth={PEN_WIDTH} strokeLinecap="round" strokeLinejoin="round">
                <g mask={`url(#ink-sigil-gaps-${id})`}>
                    {runs.under.map((run, index) => <path key={index} d={sigilRunPath(run, KNOT_RADIUS)} />)}
                </g>
                {runs.over.map((run, index) => <path key={index} d={sigilRunPath(run, KNOT_RADIUS)} />)}
            </g>
        </svg>
    );
}
